import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { PS, T } from '../lib/theme';
import { fullName } from '../lib/format';

const NAV: { path: string; label: string }[] = [
  { path: '/bookings', label: 'Bookings' },
  { path: '/users',    label: 'Users' },
  { path: '/coaches',  label: 'Coach series' },
  { path: '/coach-profiles', label: 'Public coaches' },
  { path: '/coach-callbacks', label: 'Callback requests' },
  { path: '/tournaments', label: 'Tournaments' },
  { path: '/settings', label: 'Settings' },
  { path: '/stats',    label: 'Stats' },
  { path: '/sales',    label: 'Sales' },
  { path: '/doors',    label: 'Doors' },
  { path: '/streams',  label: 'Streams' },
];

const SIDEBAR_WIDTH = 220;
const MOBILE_QUERY = '(max-width: 767px)';

export function Shell() {
  const { adminUser, signOut } = useAuth();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Auto-close the mobile drawer on route change so tapping a link doesn't
  // leave the overlay covering the new page.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // If the viewport grows past the breakpoint while the drawer is open,
  // close it so we don't render an overlay on top of the desktop sidebar.
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  const closeNav = useCallback(() => setNavOpen(false), []);

  // When the drawer is closed it's only translated off-screen, so its links
  // stay in the DOM and focusable. Toggling `aria-hidden` here is unsafe: if a
  // NavLink is tapped, the route change closes the drawer while that link still
  // holds focus, hiding a focused element from assistive tech. `inert` removes
  // the subtree from the a11y tree AND blurs/blocks focus, which is the
  // spec-recommended fix. It isn't in React 18's JSX types yet, so apply it via
  // a spread that's only present when closed (an `inert="false"` attribute
  // would still be inert — the attribute's mere presence is what counts).
  const inertWhenClosed = (navOpen ? {} : { inert: '' }) as Record<string, unknown>;

  const sidebar = (
    <aside
      style={{
        width: SIDEBAR_WIDTH,
        background: PS.nearBlack,
        color: PS.white,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 0',
        gap: 4,
        flexShrink: 0,
      }}
    >
      <div style={{ padding: '0 24px 16px', ...T.subheading, color: PS.white }}>
        ProSpin Admin
      </div>
      {NAV.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/'}
          onClick={closeNav}
          style={({ isActive }) => ({
            display: 'block',
            padding: '12px 24px',
            color: isActive ? PS.red : PS.white,
            background: isActive ? '#000000' : 'transparent',
            borderLeft: `3px solid ${isActive ? PS.red : 'transparent'}`,
            ...T.navLink,
            textTransform: 'uppercase' as const,
            // Touch-friendly hit area for mobile.
            minHeight: 44,
            boxSizing: 'border-box',
          })}
        >
          {item.label}
        </NavLink>
      ))}
    </aside>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Desktop: sidebar in normal flow. */}
      {!isMobile && sidebar}

      {/* Mobile: drawer (sidebar) + backdrop, fixed-positioned overlay. */}
      {isMobile && (
        <>
          <div
            onClick={closeNav}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              opacity: navOpen ? 1 : 0,
              pointerEvents: navOpen ? 'auto' : 'none',
              transition: 'opacity 180ms ease-out',
              zIndex: 50,
            }}
            aria-hidden={!navOpen}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              transform: navOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_WIDTH + 4}px)`,
              transition: 'transform 200ms ease-out',
              zIndex: 51,
              boxShadow: navOpen ? '4px 0 12px rgba(0,0,0,0.25)' : 'none',
            }}
            role="dialog"
            aria-label="Navigation"
            {...inertWhenClosed}
          >
            {sidebar}
          </div>
        </>
      )}

      <main style={{ flex: 1, minWidth: 0, background: PS.pageBg }}>
        <header
          style={{
            background: PS.white,
            borderBottom: `1px solid ${PS.borderLight}`,
            padding: isMobile ? '10px 12px' : '12px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            // Sticky so the hamburger stays reachable on long pages.
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          {isMobile ? (
            <button
              onClick={() => setNavOpen((o) => !o)}
              aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={navOpen}
              style={{
                background: 'transparent',
                border: `1px solid ${PS.border}`,
                borderRadius: 3,
                width: 40,
                height: 40,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <span style={hamburgerLine} />
              <span style={hamburgerLine} />
              <span style={hamburgerLine} />
            </button>
          ) : (
            <span aria-hidden />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span
              style={{
                ...T.ui,
                color: PS.textSec,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? 140 : 'none',
              }}
            >
              {isMobile
                ? (adminUser ? fullName(adminUser) : '—')
                : `${adminUser ? fullName(adminUser) : '—'}${adminUser?.email ? ` · ${adminUser.email}` : ''}`}
            </span>
            <button
              onClick={() => void signOut()}
              style={{
                background: 'transparent',
                border: `1px solid ${PS.border}`,
                padding: '8px 12px',
                borderRadius: 3,
                cursor: 'pointer',
                minHeight: 40,
                ...T.navLink,
                textTransform: 'uppercase' as const,
              }}
            >
              {isMobile ? 'Out' : 'Sign out'}
            </button>
          </div>
        </header>
        <div style={{ padding: isMobile ? 16 : 24 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

const hamburgerLine: CSSProperties = {
  display: 'block',
  width: 20,
  height: 2,
  background: PS.nearBlack,
};
